import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ManagementContentSkeleton } from '../layout/ManagementPageSkeleton';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConsentFormCard from './ConsentFormCard';
import ConsentFormEditorDialog from './ConsentFormEditorDialog';
import ConsentFormUploadDialog from './ConsentFormUploadDialog';
import { getConsentFormErrorKey, getConsentFormName } from './consentFormDisplay';
import { useToast } from '../../context/ToastContext';
import ConsentFormService from '../../services/consentFormService';

const ConsentFormViewerDialog = lazy(() => import('./ConsentFormViewerDialog'));

const CLOSED_EDITOR = { open: false, mode: 'create', consentForm: null };

const ConsentFormsPanel = ({ storeId, canWrite, supportedLanguages, onChanged }) => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editor, setEditor] = useState(CLOSED_EDITOR);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyFormId, setBusyFormId] = useState(null);

  const loadForms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setForms(await ConsentFormService.getConsentForms(storeId));
    } catch (loadError) {
      console.error('Failed to load consent forms:', loadError);
      setError(getConsentFormErrorKey(loadError, 'consentForms.errors.loading'));
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const refresh = useCallback(async () => {
    setForms(await ConsentFormService.getConsentForms(storeId));
    onChanged?.();
  }, [onChanged, storeId]);

  const handleSaveForm = async (formData) => {
    const isEdit = editor.mode === 'edit' && editor.consentForm?.id;

    try {
      setSaving(true);
      if (isEdit) {
        await ConsentFormService.updateConsentForm(storeId, editor.consentForm.id, formData);
        showSuccess(t('consentForms.management.feedback.updated'));
      } else {
        const created = await ConsentFormService.createConsentForm(storeId, formData);
        try {
          await ConsentFormService.uploadConsentFormVersion(storeId, created.id, formData.file, true);
          showSuccess(t('consentForms.management.feedback.created'));
        } catch (uploadError) {
          console.error('Failed to upload the first consent form version:', uploadError);
          showError(t(getConsentFormErrorKey(uploadError, 'consentForms.errors.upload')));
        }
      }

      setEditor(CLOSED_EDITOR);
      await refresh();
    } catch (saveError) {
      console.error('Failed to save consent form:', saveError);
      showError(t(getConsentFormErrorKey(saveError, isEdit ? 'consentForms.errors.update' : 'consentForms.errors.create')));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file, requiresReacceptance) => {
    try {
      setSaving(true);
      await ConsentFormService.uploadConsentFormVersion(storeId, uploadTarget.id, file, requiresReacceptance);
      showSuccess(t('consentForms.management.feedback.uploaded'));
      setUploadTarget(null);
      await refresh();
    } catch (uploadError) {
      console.error('Failed to upload consent form version:', uploadError);
      showError(t(getConsentFormErrorKey(uploadError, 'consentForms.errors.upload')));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (consentForm, archive) => {
    try {
      setBusyFormId(consentForm.id);
      if (archive) {
        await ConsentFormService.archiveConsentForm(storeId, consentForm.id);
      } else {
        await ConsentFormService.restoreConsentForm(storeId, consentForm.id);
      }
      showSuccess(t(archive ? 'consentForms.management.feedback.archived' : 'consentForms.management.feedback.restored'));
      await refresh();
    } catch (statusError) {
      console.error('Failed to change consent form status:', statusError);
      showError(t(getConsentFormErrorKey(statusError, archive ? 'consentForms.errors.archive' : 'consentForms.errors.restore')));
    } finally {
      setBusyFormId(null);
    }
  };

  const loadPreviewFile = useCallback(
    (versionId) => ConsentFormService.getManagementConsentFormFile(storeId, versionId),
    [storeId],
  );

  if (loading) {
    return <ManagementContentSkeleton variant="cards" withAction={canWrite} />;
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{t(error)}</Alert>
        <Button id="consent-forms-retry" variant="contained" onClick={loadForms}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box id="consent-forms-content" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: { xs: 2.5, sm: 4 },
          pt: { xs: 0.5, sm: 1 },
        }}
      >
        <Typography id="consent-forms-intro" variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          {t('consentForms.management.intro')}
        </Typography>
        {canWrite ? (
          <PrimaryActionButton
            id="consent-forms-add"
            startIcon={<AddIcon />}
            onClick={() => setEditor({ open: true, mode: 'create', consentForm: null })}
            sx={{ px: { xs: 2.5, sm: 4 }, py: 1.5, width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
          >
            {t('consentForms.management.addNew')}
          </PrimaryActionButton>
        ) : null}
      </Box>

      {forms.length === 0 ? (
        <Box id="consent-forms-empty" sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('consentForms.management.noForms')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('consentForms.management.emptyHint')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
          {forms.map((consentForm) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={consentForm.id}>
              <ConsentFormCard
                consentForm={consentForm}
                canEdit={canWrite}
                loading={saving || busyFormId === consentForm.id}
                onEdit={() => setEditor({ open: true, mode: 'edit', consentForm })}
                onUpload={() => setUploadTarget(consentForm)}
                onPreview={() => setPreviewForm(consentForm)}
                onArchive={() => setArchiveTarget(consentForm)}
                onRestore={() => changeStatus(consentForm, false)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ConsentFormEditorDialog
        open={editor.open}
        mode={editor.mode}
        consentForm={editor.consentForm}
        supportedLanguages={supportedLanguages}
        saving={saving}
        onClose={() => setEditor(CLOSED_EDITOR)}
        onSave={handleSaveForm}
      />

      <ConsentFormUploadDialog
        open={Boolean(uploadTarget)}
        consentForm={uploadTarget}
        saving={saving}
        onClose={() => setUploadTarget(null)}
        onUpload={handleUpload}
      />

      <ConfirmDialog
        open={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        onConfirm={async () => {
          const target = archiveTarget;
          setArchiveTarget(null);
          await changeStatus(target, true);
        }}
        title={t('consentForms.management.archive.title')}
        message={t('consentForms.management.archive.message', {
          name: archiveTarget ? getConsentFormName(archiveTarget.name, i18n.language, t) : '',
        })}
        confirmText={t('consentForms.management.archive.confirm')}
        cancelText={t('common.cancel')}
        confirmColor="error"
      />

      {previewForm?.currentVersion ? (
        <Suspense fallback={null}>
          <ConsentFormViewerDialog
            open
            preview
            forms={[{
              consentFormId: previewForm.id,
              name: previewForm.name,
              versionId: previewForm.currentVersion.id,
              versionNumber: previewForm.currentVersion.versionNumber,
              serviceIds: previewForm.serviceIds,
            }]}
            loadFile={loadPreviewFile}
            onCancel={() => setPreviewForm(null)}
            onAccept={() => setPreviewForm(null)}
          />
        </Suspense>
      ) : null}
    </Box>
  );
};

export default ConsentFormsPanel;
